import {
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Button,
  Box,
  InputLabel,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { locations } from "../../../constants";
import { formatDate } from "../../../utils/utils";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { useFormikContext } from "formik";
import { IncomeFormData } from "../types";
import { smallButtonProp } from "../../../utils/styles";

const getPaymentLocations = () => {
  return locations.map((location) => ({
    value: location.toLowerCase(),
    label: location,
  }));
};

const PaymentDetails = ({
  values,
  handleChange,
  setFieldValue,
  calculateAmounts,
  incomeSummary,
  index,
}: any) => {
  const [addRemarks, setAddRemarks] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const checkDisabled = () => {
    if (!values?.paymentDetails[0]?.id) return false;
    return !isEditMode;
  };

  const toggleRemarks = () => {
    setAddRemarks(!addRemarks);
  };

  const handleDeletePayment = () => {
    const currentPayment = values.paymentDetails[0];

    if (currentPayment?.id) {
      // If payment has an ID, mark it as deleted by setting deletedAt
      setFieldValue(
        `incomes.${index}.paymentDetails.0.deletedAt`,
        new Date().toISOString()
      );
      setFieldValue(`incomes.${index}.paymentDetails.0.isChanged`, true);
    } else {
      // If no payment ID, remove it directly
      setFieldValue(`incomes.${index}.paymentDetails`, []);
    }

    // Recalculate amounts
    if (calculateAmounts) {
      calculateAmounts();
    }
  };

  const handleUndoDelete = () => {
    const currentPayment = values.paymentDetails[0];

    if (currentPayment?.deletedAt) {
      // Restore the deleted payment by clearing deletedAt
      setFieldValue(`incomes.${index}.paymentDetails.0.deletedAt`, undefined);
      setFieldValue(`incomes.${index}.paymentDetails.0.isChanged`, true);
    }

    // Recalculate amounts
    if (calculateAmounts) {
      calculateAmounts();
    }
  };

  const handleAddPayment = () => {
    const newPayment = {
      type: "ADVANCE",
      paymentDate: new Date().toISOString().split("T")[0],
      paymentMode: "CASH",
      paymentAmount: 0,
      paymentLocation: "",
      paymentReceivedFrom: "",
      remarks: "",
      isNew: true,
      status: "PENDING",
      ...(values?.id && {
        rentalJourneyIncomeId: values?.id,
      }),
    };

    setFieldValue(`incomes.${index}.paymentDetails`, [newPayment]);
  };

  // If no payment details exist, show add payment button
  if (!values.paymentDetails || values.paymentDetails.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2">Payment Details</Typography>
        <Button
          // variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddPayment}
          size="small"
          sx={{ ...smallButtonProp?.sx, mt: 1 }}
        >
          Add Payment
        </Button>
      </Box>
    );
  }

  // Get the first (and only) payment
  const payment = values.paymentDetails[0];
  const isDeleted = Boolean(payment?.deletedAt);

  return (
    <Box sx={{ mt: 1 }}>
      {/* Horizontal form fields */}
      <Grid container spacing={2} alignItems="flex-start">
        <Grid container item xs={12} spacing={1} alignItems="center" mt={1}>
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2">Payment Details</Typography>
          </Grid>
          <Grid
            item
            md={4}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            {!isEditMode && values?.paymentDetails[0]?.id && (
              <Button
                size="small"
                sx={{
                  height: "20px",
                  width: "50px",
                  fontSize: "0.6rem",
                }}
                variant="contained"
                color="primary"
                onClick={() => {
                  setIsEditMode(true);
                }}
              >
                Edit
              </Button>
            )}
            {isEditMode && values?.paymentDetails[0]?.id && (
              <Button
                size="small"
                sx={{
                  height: "20px",
                  width: "50px",
                  fontSize: "0.6rem",
                }}
                variant="contained"
                color="error"
                onClick={() => {
                  setIsEditMode(false);
                }}
              >
                Cancel
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            required
            fullWidth
            size="small"
            type="date"
            label="Date"
            value={
              new Date(payment?.paymentDate).toISOString().split("T")[0] || ""
            }
            onChange={(e) =>
              handleChange(
                values,
                "paymentDetails.paymentDate",
                e.target.value,
                0,
                setFieldValue
              )
            }
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiInputBase-root": { fontSize: "0.75rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
            }}
            disabled={checkDisabled()}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel
              id="payment-mode-label"
              shrink
              sx={{ fontSize: "0.75rem" }}
            >
              Mode*
            </InputLabel>
            <Select
              labelId="payment-mode-label"
              label="Mode"
              value={payment?.paymentMode || ""}
              onChange={(e) =>
                handleChange(
                  values,
                  "paymentDetails.paymentMode",
                  e.target.value,
                  0,
                  setFieldValue
                )
              }
              sx={{
                backgroundColor: "#fff",
                fontSize: "0.75rem",
              }}
              required
              disabled={checkDisabled()}
            >
              <MenuItem value="CASH" sx={{ fontSize: "0.75rem" }}>
                Cash
              </MenuItem>
              <MenuItem value="ONLINE" sx={{ fontSize: "0.75rem" }}>
                Online
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={2}>
          <TextField
            required
            fullWidth
            size="small"
            label="Amount"
            value={
              payment?.paymentAmount && payment.paymentAmount >= 0
                ? Number(payment.paymentAmount).toLocaleString("en-IN")
                : ""
            }
            onChange={(e) => {
              const cleanValue = e.target.value.replace(/[^0-9]/g, "");
              handleChange(
                values,
                "paymentDetails.paymentAmount",
                cleanValue,
                0,
                setFieldValue
              );
            }}
            onBlur={() => {
              if (calculateAmounts) calculateAmounts();
            }}
            error={Boolean(
              payment?.paymentAmount > incomeSummary?.amountAfterGst
            )}
            helperText={
              payment?.paymentAmount > incomeSummary?.amountAfterGst
                ? "Payment amount cannot exceed total amount"
                : ""
            }
            InputProps={{
              startAdornment: (
                <Typography
                  component="span"
                  sx={{ mr: 0.5, fontSize: "0.75rem" }}
                >
                  ₹
                </Typography>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": { fontSize: "0.75rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
              "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
            }}
            disabled={checkDisabled()}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <TextField
            fullWidth
            size="small"
            label="Location"
            placeholder="Location"
            value={payment?.paymentLocation || ""}
            onChange={(e) =>
              handleChange(
                values,
                "paymentDetails.paymentLocation",
                e.target.value,
                0,
                setFieldValue
              )
            }
            sx={{
              "& .MuiInputBase-root": { fontSize: "0.75rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
            }}
            disabled={checkDisabled()}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          {isDeleted ? (
            <Button
              size="small"
              sx={{
                ml: 1,
                p: 0.5,
                fontSize: "0.75rem",
                fontStyle: "italic",
              }}
              onClick={() => {
                handleUndoDelete();
              }}
            >
              Undo
            </Button>
          ) : (
            <IconButton
              color="error"
              onClick={handleDeletePayment}
              title="Delete payment"
              size="small"
            >
              <DeleteIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
        </Grid>
      </Grid>

      {/* Add Remarks button */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-start" }}>
        <Button variant="outlined" onClick={toggleRemarks} {...smallButtonProp}>
          {addRemarks ? "Hide Remarks" : "Add Remarks"}
        </Button>
      </Box>

      {/* Remarks field */}
      {addRemarks && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Remarks"
            placeholder="Enter remarks..."
            value={payment?.remarks || ""}
            onChange={(e) =>
              handleChange(
                values,
                "paymentDetails.remarks",
                e.target.value,
                0,
                setFieldValue
              )
            }
            multiline
            rows={2}
            sx={{
              "& .MuiInputBase-root": { fontSize: "0.75rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
            }}
          />
        </Grid>
      )}
    </Box>
  );
};

export default PaymentDetails;
